// src/components/HeroSectionAbout.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AboutImageSlideshow from './AboutImageSlideshow';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

function HeroSectionAbout({ isAdmin, onLogout }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [textContent, setTextContent] = useState({
    section1: 'Naša spoločnosť sa špecializuje na kompletné služby v oblasti nehnuteľností. Ponúkame predaj, prenájom a kúpu bytov, domov, pozemkov a komerčných nehnuteľností v Prievidzi a okolí.',
    section2: 'S dlhoročnými skúsenosťami a individuálnym prístupom k každému klientovi vám pomôžeme nájsť ideálnu nehnuteľnosť podľa vašich potrieb a finančných možností.'
  });
  const [isLoading, setIsLoading] = useState(true);
  const [editingSection, setEditingSection] = useState(null);
  const [editedText, setEditedText] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load text content on component mount
  useEffect(() => {
    loadTextContent();
  }, []);

  const loadTextContent = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('https://eurocentrumv1.onrender.com/api/about-text');
      if (response.ok) {
        const data = await response.json();
        setTextContent(data);
      }
    } catch (error) {
      console.error('Error loading text content:', error);
      // Keep default content if loading fails
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = (section) => {
    if (!isAdmin) return;
    setEditingSection(section);
    setEditedText(textContent[section]);
  };

  const handleSaveText = async () => {
    if (!editingSection) return;
    
    try {
      setIsSaving(true);
      const response = await fetch('https://eurocentrumv1.onrender.com/api/about-text', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          section: editingSection,
          text: editedText
        }),
      });

      if (response.ok) {
        const updatedData = await response.json();
        setTextContent(updatedData);
        setEditingSection(null);
        setEditedText('');
      } else {
        alert('Chyba pri ukladaní textu');
      }
    } catch (error) {
      console.error('Error saving text:', error);
      alert('Chyba pri ukladaní textu');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingSection(null);
    setEditedText('');
  };

  return (
    <div style={heroStyles.mainContainer}>
      {/* Main Grid Container */}
      <div style={{
        ...heroStyles.gridContainer,
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        {/* Left Column */}
        <div style={heroStyles.column}>
          {/* Left Image */}
          <div style={heroStyles.imageContainer}>
            <AboutImageSlideshow
              section="hero-about-left"
              isAdmin={isAdmin}
              customImageStyle={heroStyles.gridImage}
              customContainerStyle={heroStyles.gridImageContainer}
            />
            {/* Dark Overlay for left image */}
            <div style={heroStyles.overlay}></div>
            
            {/* Left Side Hero Content */}
            <div style={heroStyles.leftHeroContent}>
              <h1 style={isMobile ? heroStyles.titleMobile : heroStyles.title}>O NÁS</h1>
              <p style={isMobile ? heroStyles.subtitleMobile : heroStyles.subtitle}>
                Investujte zmysluplne. Využite bezplatné služby nášho poradenstva.
              </p>
              
              <div style={isMobile ? heroStyles.buttonWrapperMobile : heroStyles.buttonWrapper}>
                <Link to="/" style={isMobile ? heroStyles.buttonLinkMobile : heroStyles.buttonLink}>
                  <button style={isMobile ? heroStyles.heroButtonBlueMobile : heroStyles.heroButtonBlue}>
                    Naša ponuka
                  </button>
                </Link>
                
                <Link to="/contact" style={isMobile ? heroStyles.buttonLinkMobile : heroStyles.buttonLink}>
                  <button style={isMobile ? heroStyles.heroButtonWhiteMobile : heroStyles.heroButtonWhite}>
                    Kontaktujte nás
                  </button>
                </Link>
              </div>
            </div>
          </div>
          
          {/* Left Text Container */}
          <div style={heroStyles.textContainer}>
            {editingSection === 'section1' ? (
              <div style={heroStyles.editContainer}>
                <ReactQuill
                  value={editedText}
                  onChange={setEditedText}
                  style={heroStyles.textEditor}
                  theme="snow"
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                      ['link'],
                      ['clean']
                    ],
                  }}
                />
                <div style={heroStyles.editButtons}>
                  <button
                    onClick={handleSaveText}
                    disabled={isSaving}
                    style={heroStyles.saveButton}
                  >
                    {isSaving ? 'Ukladám...' : 'Uložiť'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    style={heroStyles.cancelButton}
                  >
                    Zrušiť
                  </button>
                </div>
              </div>
            ) : (
              <div 
                style={heroStyles.textContent}
                onClick={() => handleEditClick('section1')}
              >
                <div
                  style={heroStyles.textParagraph}
                  dangerouslySetInnerHTML={{ __html: isLoading ? 'Načítavam...' : textContent.section1 }}
                />
                {isAdmin && (
                  <div style={heroStyles.editIcon}>
                    ✏️ Upraviť text
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Column */}
        <div style={heroStyles.column}>
          {/* Right Image */}
          <div style={heroStyles.imageContainer}>
            <AboutImageSlideshow
              section="hero-about-right"
              isAdmin={isAdmin}
              customImageStyle={heroStyles.gridImage}
              customContainerStyle={heroStyles.gridImageContainer}
            />
            {/* Dark Overlay for right image */}
            <div style={heroStyles.overlay}></div>
          </div>
          
          {/* Right Text Container */}
          <div style={heroStyles.textContainer}>
            {editingSection === 'section2' ? (
              <div style={heroStyles.editContainer}>
                <ReactQuill
                  value={editedText}
                  onChange={setEditedText}
                  style={heroStyles.textEditor}
                  theme="snow"
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                      ['link'],
                      ['clean']
                    ],
                  }}
                />
                <div style={heroStyles.editButtons}>
                  <button
                    onClick={handleSaveText}
                    disabled={isSaving}
                    style={heroStyles.saveButton}
                  >
                    {isSaving ? 'Ukladám...' : 'Uložiť'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    style={heroStyles.cancelButton}
                  >
                    Zrušiť
                  </button>
                </div>
              </div>
            ) : (
              <div 
                style={heroStyles.textContent}
                onClick={() => handleEditClick('section2')}
              >
                <div
                  style={heroStyles.textParagraph}
                  dangerouslySetInnerHTML={{ __html: isLoading ? 'Načítavam...' : textContent.section2 }}
                />
                {isAdmin && (
                  <div style={heroStyles.editIcon}>
                    ✏️ Upraviť text
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default HeroSectionAbout;

const heroStyles = {
  mainContainer: {
    fontFamily: "'Inter', Helvetica, Arial, sans-serif",
    width: '100%',
  },
  gridContainer: {
    display: 'flex',
    width: '100%',
    margin: '0',
    gap: '0',
  },
  column: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  imageContainer: {
    position: 'relative',
    width: '100%',
    height: '33vh',
    minHeight: '200px',
    color: '#fff',
    overflow: 'hidden',
  },
  textContainer: {
    minHeight: '200px',
    backgroundColor: '#f9f9f9',
    padding: '1.5rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  gridImageContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  gridImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 2,
  },
  leftHeroContent: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
    width: '90%',
    maxWidth: '400px',
    padding: '0 15px',
    boxSizing: 'border-box',
    zIndex: 3,
  },
  title: {
    fontSize: '2.5rem',
    margin: '0 0 0.5rem',
    fontWeight: 'bold',
  },
  titleMobile: {
    fontSize: '1.8rem',
    margin: '0 0 0.5rem',
    fontWeight: 'bold',
  },
  subtitle: {
    fontSize: '0.9rem',
    marginBottom: '1rem',
  },
  subtitleMobile: {
    fontSize: '0.8rem',
    marginBottom: '0.8rem',
    padding: '0 10px',
  },
  buttonWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.3rem',
    justifyContent: 'center',
  },
  buttonWrapperMobile: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.3rem',
    width: '100%',
  },
  buttonLink: {
    textDecoration: 'none',
  },
  buttonLinkMobile: {
    textDecoration: 'none',
    width: '100%',
  },
  heroButtonBlue: {
    fontSize: '0.7rem',
    padding: '0.4rem 0.8rem',
    backgroundColor: '#2c69a0',
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '0.3rem',
    transition: 'transform 0.3s ease, background-color 0.3s ease',
  },
  heroButtonBlueMobile: {
    fontSize: '0.7rem',
    padding: '0.4rem 0.6rem',
    width: '100%',
    backgroundColor: '#2c69a0',
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '0.3rem',
    transition: 'transform 0.3s ease, background-color 0.3s ease',
  },
  heroButtonWhite: {
    fontSize: '0.7rem',
    padding: '0.4rem 0.8rem',
    backgroundColor: '#fff',
    color: '#000',
    cursor: 'pointer',
    borderRadius: '0.3rem',
    border: 'none',
    transition: 'transform 0.3s ease, background-color 0.3s ease',
  },
  heroButtonWhiteMobile: {
    fontSize: '0.7rem',
    padding: '0.4rem 0.6rem',
    width: '100%',
    backgroundColor: '#fff',
    color: '#000',
    cursor: 'pointer',
    borderRadius: '0.3rem',
    border: 'none',
    transition: 'transform 0.3s ease, background-color 0.3s ease',
  },
  
  // Text Content Styles
  textContent: {
    cursor: 'pointer',
    position: 'relative',
    padding: '1rem',
    borderRadius: '4px',
    transition: 'background-color 0.2s ease',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  textParagraph: {
    fontSize: '1rem',
    lineHeight: '1.5',
    color: '#333',
    margin: 0,
    textAlign: 'center',
  },
  editIcon: {
    position: 'absolute',
    top: '0.5rem',
    right: '0.5rem',
    fontSize: '0.8rem',
    color: '#2c69a0',
    backgroundColor: 'white',
    padding: '0.3rem 0.6rem',
    borderRadius: '4px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    opacity: 0.8,
    transition: 'opacity 0.2s ease',
  },
  editContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    width: '100%',
    minHeight: '200px',
    padding: '1rem',
  },
  textEditor: {
    width: '100%',
    minHeight: '150px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontFamily: 'inherit',
  },
  editButtons: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'flex-end',
    marginTop: '1rem',
  },
  saveButton: {
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  cancelButton: {
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.8rem',
  },
};
